import type ApplicationInstance from '@ember/application/instance';
import { getOwner } from '@ember/owner';
import Modifier from 'ember-modifier';
import mapboxgl, { type LngLatLike, type StyleSpecification } from 'mapbox-gl';

// The 3rd entry in the coordinate tuple can be used for additional data, like a label, unique ID or a custom object.
export type MapboxCustomCoordinate = [number, number, (number | string | Record<string, unknown>)?];

export interface MapboxArgs {
  center?: MapboxCustomCoordinate | undefined;
  coordinates?: MapboxCustomCoordinate[] | undefined;
  drawAllCoordinateMarkers?: boolean | undefined;
  drawFirstCoordinateMarker?: boolean | undefined;
  drawLastCoordinateMarker?: boolean | undefined;
  drawRoute?: boolean | undefined;
  onMarkerClick?: ((coordinate: MapboxCustomCoordinate) => void) | undefined;
  style?: string | StyleSpecification | undefined;
  zoom?: number | undefined;
}

interface MapboxSignature {
  Args: {
    Named: MapboxArgs;
  };
  Element: HTMLDivElement;
}

export default class MapboxModifier extends Modifier<MapboxSignature> {
  modify(element: HTMLDivElement, positional: unknown[], named: MapboxArgs) {
    if (!this.accessToken) {
      console.error('Mapbox access token is missing in the environment config.');
      return;
    }

    const { center, coordinates, onMarkerClick, style, zoom } = named;

    const map = new mapboxgl.Map({
      attributionControl: false,
      accessToken: this.accessToken,
      container: element,
      style,
      center: center && [center[0], center[1]],
      zoom,
    });

    if (!coordinates?.length) return;

    const createAndAddMarker = (coordinate: MapboxCustomCoordinate) => {
      const el = this.createCustomMarker();

      if (onMarkerClick) {
        el.style.cursor = 'pointer';
        el.addEventListener('click', () => {
          onMarkerClick(coordinate);
        });
      }

      const lngLat: LngLatLike = [coordinate[0], coordinate[1]];
      new mapboxgl.Marker({ element: el }).setLngLat(lngLat).addTo(map);
    };

    const {
      drawAllCoordinateMarkers,
      drawFirstCoordinateMarker,
      drawLastCoordinateMarker,
      drawRoute,
    } = named;

    if (drawAllCoordinateMarkers) {
      coordinates.forEach((coordinate) => {
        createAndAddMarker(coordinate);
      });
    } else {
      if (drawFirstCoordinateMarker) {
        createAndAddMarker(coordinates[0] as MapboxCustomCoordinate);
      }
      if (drawLastCoordinateMarker) {
        createAndAddMarker(coordinates[coordinates.length - 1] as MapboxCustomCoordinate);
      }
    }

    if (drawRoute) {
      const lineColor = '#499682';
      const lineData = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates.map((coordinate) => [coordinate[0], coordinate[1]]),
        },
      } as const;

      map.on('load', () => {
        map.addSource('route', {
          type: 'geojson',
          data: lineData,
        });

        map.addLayer({
          id: 'route-layer',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': lineColor,
            'line-width': 3,
          },
        });
      });
    }
  }

  get accessToken(): string {
    const owner = getOwner(this) as ApplicationInstance;

    const config = owner?.resolveRegistration('config:environment') as Record<
      string,
      // eslint-disable-next-line
      any
    >;
    // eslint-disable-next-line
    return config?.['@mov-uk/ember-shared']?.mapboxAccessToken;
  }

  createCustomMarker = () => {
    const markerColor = '#499682';
    const markerElement = document.createElement('div');

    markerElement.style.background = markerColor;
    markerElement.style.width = '12px';
    markerElement.style.height = '12px';
    markerElement.style.borderRadius = '50%';

    return markerElement;
  };
}
